import {useMemo} from 'react';
import isEmpty from '@tinkoff/utils/is/empty';

import {ChannelType} from '@mattermost/types/channels';
import {getThreadPostFollowState} from 'features/threads/selectors/get_thread_post_follow_state';
import {selectThreadOrSyntheticThread} from 'features/threads/selectors/select_thread_or_synthetic_thread';
import {getReadOnlyStateByChannelId} from 'features/channels';
import {getConfig, getLicense} from 'mattermost-redux/selectors/entities/general';
import {getChannel, getMyChannelMemberships} from 'mattermost-redux/selectors/entities/channels';
import {getCurrentUser} from 'mattermost-redux/selectors/entities/common';
import {getCurrentTeam, getTeam} from 'mattermost-redux/selectors/entities/teams';
import {haveIChannelPermission} from 'mattermost-redux/selectors/entities/roles';
import Permissions from 'mattermost-redux/constants/permissions';
import {isSelfTalkChannel} from 'mattermost-redux/utils/channel_utils';
import {getPost} from 'mattermost-redux/selectors/entities/posts';
import {get} from 'mattermost-redux/selectors/entities/preferences';
import {useAppShallowEqualSelector} from 'stores/redux_store';
import type {GlobalState} from 'types/store';
import {Preferences} from 'utils/constants';
import {getSiteURL} from 'utils/url';
import * as PostUtils from 'utils/post_utils';

import type {UsePostMenuItemsParams} from './types';
import {isCenterOrThreadLocation, isThreadLocation} from './utils';

/* istanbul ignore next */
export function usePostMenuData(params: UsePostMenuItemsParams) {
    const {post, location} = params;

    const getReactionsForPost = useMemo(() => PostUtils.makeGetUniqueReactionsToPost(), []);

    return useAppShallowEqualSelector((state: GlobalState) => {
        const config = getConfig(state);
        const license = getLicense(state);
        const channel = getChannel(state, post.channel_id)!;
        const currentUser = getCurrentUser(state)!;
        const currentTeam = getCurrentTeam(state);
        const team = getTeam(state, channel.team_id);
        const teamId = team?.id || currentTeam?.id || channel.team_id;

        const canIAddEmoji = haveIChannelPermission(state, teamId, channel.id, Permissions.ADD_REACTION);
        const canCreatePost = haveIChannelPermission(state, teamId, channel.id, Permissions.CREATE_POST);
        const canCreatePostInThread = haveIChannelPermission(state, teamId, channel.id, Permissions.CREATE_POST_IN_THREAD);

        const isChannelMember = Boolean(getMyChannelMemberships(state)[channel.id]);
        const isOpenChannelWithMyMembership = channel.type === ChannelType.Open && isChannelMember;
        const isChannelWithReactionModal = isOpenChannelWithMyMembership || channel.type === ChannelType.Private;
        const isArchivedChannel = channel.delete_at > 0;
        const canReply = isThreadLocation(location) ? canCreatePostInThread : canCreatePost;
        const rootId = post.root_id || post.id;

        let threadId = rootId;
        let threadReplyCount = 0;

        if (rootId && isCenterOrThreadLocation(location)) {
            const rootPost = getPost(state, rootId);

            if (rootPost) {
                const thread = selectThreadOrSyntheticThread(state, rootPost);
                threadReplyCount = thread.reply_count;
                threadId = thread.id;
            }
        }

        return {
            canDelete: PostUtils.canDeletePost(state, post, channel) && !isArchivedChannel,
            canEdit: PostUtils.canEditPost(state, post, license, config, channel, currentUser.id) && !isArchivedChannel,
            canReply,
            channelName: channel.name,
            channelType: channel.type,
            currentUser,
            disableEditAfterTimeLimit: !isSelfTalkChannel(currentUser.id, channel),
            enableEmojiPicker: config.EnableEmojiPicker === 'true' && !isArchivedChannel && canIAddEmoji,
            hasReaction: isChannelWithReactionModal && !isEmpty(getReactionsForPost(state, post.id)),
            isArchivedChannel,
            isFlaggedPost: get(state, Preferences.CATEGORY_FLAGGED_POST, post.id, null) != null,
            isFollowingThread: getThreadPostFollowState(state, rootId),
            isLicensed: license.IsLicensed === 'true',
            isReadOnlyChannel: getReadOnlyStateByChannelId(state, channel.id),
            pluginItems: state.plugins.components.PostDropdownMenu,
            postEditTimeLimit: config.PostEditTimeLimit,
            teamId,
            teamName: team?.name || currentTeam?.name || '',
            teamUrl: `${getSiteURL()}/${team?.name || currentTeam?.name}`,
            threadId,
            threadReplyCount,
        };
    });
}
